import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAppContext } from '../../context/AppContext';
import {
  Smartphone, Wifi, WifiOff, QrCode, Nfc, Radio, User,
  AlertTriangle, Heart, Pill, Utensils, Droplets, RotateCcw,
  CheckCircle, Clock, Mic, MicOff, Camera, FileText, Plus,
  Shield, Activity, Thermometer, ChevronRight, X, Battery,
  MapPin, Send, Save, Clipboard, Calendar, Eye, Zap, LogOut,
  Search, Bell, Home, ClipboardList, MoreHorizontal, Settings,
  RefreshCw, ArrowDown
} from 'lucide-react';
import MobileAppService from '../../services/mobile/MobileAppService';
import HardwareConfigService from '../../services/mobile/HardwareConfigService';
import { useToast } from '../Common/ToastProvider';
import { Tooltip } from '../Common/ReusableUI';

// ─── Constants ────────────────────────────────────────────────────────

const RECENT_RESIDENTS_KEY = 'mfac_recent_residents';

const NAV_ITEMS = [
  { id: 'scan', label: 'Scan', icon: QrCode },
  { id: 'tasks', label: 'Tasks', icon: ClipboardList },
  { id: 'chart', label: 'Chart', icon: FileText },
  { id: 'mar', label: 'MAR', icon: Pill },
  { id: 'more', label: 'More', icon: MoreHorizontal },
];

// ─── Utilities ────────────────────────────────────────────────────────

const haptic = (ms = 50) => {
  try { navigator.vibrate?.(ms); } catch (e) { /* unsupported */ }
};

const getRecentResidents = () => {
  try {
    return JSON.parse(localStorage.getItem(RECENT_RESIDENTS_KEY) || '[]');
  } catch {
    return [];
  }
};

const saveRecentResident = (resident) => {
  try {
    const recents = getRecentResidents().filter(r => r.id !== resident.id);
    recents.unshift({
      id: resident.id,
      name: resident.name,
      preferredName: resident.preferredName,
      room: resident.room,
      alerts: resident.alerts,
      allergies: resident.allergies,
      diet: resident.diet,
      mobility: resident.mobility,
      communication: resident.communication,
      carePlanSummary: resident.carePlanSummary,
      todaysTasks: resident.todaysTasks,
    });
    localStorage.setItem(RECENT_RESIDENTS_KEY, JSON.stringify(recents.slice(0, 5)));
    console.debug('[MobileCarerApp] Recent residents updated', recents.length);
  } catch (e) {
    console.warn('[MobileCarerApp] Failed to save recent resident', e);
  }
};

// ─── Pull-to-Refresh Wrapper ──────────────────────────────────────────

const PullToRefresh = ({ onRefresh, children }) => {
  const containerRef = useRef(null);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const touchStartY = useRef(0);
  const isPulling = useRef(false);

  const handleTouchStart = useCallback((e) => {
    if (containerRef.current && containerRef.current.scrollTop <= 0) {
      touchStartY.current = e.touches[0].clientY;
      isPulling.current = true;
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isPulling.current || refreshing) return;
    const delta = e.touches[0].clientY - touchStartY.current;
    if (delta > 0) {
      setPullDistance(Math.min(delta * 0.5, 120));
    }
  }, [refreshing]);

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling.current) return;
    isPulling.current = false;
    if (pullDistance > 80 && !refreshing) {
      setRefreshing(true);
      console.info('[PullToRefresh] Refreshing...');
      try {
        await onRefresh();
      } finally {
        setRefreshing(false);
      }
    }
    setPullDistance(0);
  }, [pullDistance, refreshing, onRefresh]);

  return (
    <div
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      className="relative"
    >
      {(pullDistance > 0 || refreshing) && (
        <div
          className="flex items-center justify-center overflow-hidden transition-all"
          style={{ height: refreshing ? 48 : pullDistance }}
        >
          {refreshing ? (
            <RefreshCw className="w-5 h-5 text-indigo-600 animate-spin" />
          ) : (
            <ArrowDown
              className="w-5 h-5 text-gray-400 transition-transform duration-200"
              style={{ transform: `rotate(${Math.min(pullDistance / 80, 1) * 180}deg)`, opacity: Math.min(pullDistance / 40, 1) }}
            />
          )}
        </div>
      )}
      {children}
    </div>
  );
};

// ─── Swipeable Task Item ──────────────────────────────────────────────

const SwipeableTaskItem = ({ children, onSwipeComplete, disabled }) => {
  const [translateX, setTranslateX] = useState(0);
  const touchStartX = useRef(0);
  const swiping = useRef(false);

  const handleTouchStart = useCallback((e) => {
    if (disabled) return;
    touchStartX.current = e.touches[0].clientX;
    swiping.current = true;
  }, [disabled]);

  const handleTouchMove = useCallback((e) => {
    if (!swiping.current) return;
    const delta = e.touches[0].clientX - touchStartX.current;
    if (delta > 0) {
      setTranslateX(Math.min(delta, 150));
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!swiping.current) return;
    swiping.current = false;
    if (translateX > 100) {
      console.info('[SwipeableTaskItem] Swipe threshold reached — completing');
      onSwipeComplete();
    }
    setTranslateX(0);
  }, [translateX, onSwipeComplete]);

  return (
    <div className="relative overflow-hidden rounded-xl">
      <div
        className="absolute inset-y-0 left-0 bg-green-500 flex items-center pl-4 rounded-l-xl"
        style={{ width: Math.max(translateX, 0), opacity: Math.min(translateX / 60, 1) }}
      >
        {translateX > 40 && (
          <div className="flex items-center gap-2 text-white font-medium text-sm whitespace-nowrap">
            <CheckCircle className="w-5 h-5" />
            {translateX > 80 && <span>Complete</span>}
          </div>
        )}
      </div>
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: `translateX(${translateX}px)`,
          transition: swiping.current ? 'none' : 'transform 0.3s ease',
        }}
      >
        {children}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────

const MobileCarerApp = () => {
  const { state } = useAppContext();
  const { deviceInfo, currentUser } = state;
  const { optimization } = deviceInfo;
  const { showToast } = useToast();

  const [activeTab, setActiveTab] = useState('scan');
  const [moreSubPanel, setMoreSubPanel] = useState(null);
  const [isOnline, setIsOnline] = useState(true);
  const [scannedResident, setScannedResident] = useState(null);
  const [scanStartTime, setScanStartTime] = useState(null);
  const [careMinutesLog, setCareMinutesLog] = useState([]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    setIsOnline(navigator.onLine);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    MobileAppService.initializeMobileApp(currentUser?.id || 'demo-carer');
  }, [currentUser]);

  const handleScanIn = useCallback((resident) => {
    setScannedResident(resident);
    setScanStartTime(new Date());
    setActiveTab('tasks');
    setMoreSubPanel(null);
    saveRecentResident(resident);
    haptic(50);
    showToast(`Scanned in: ${resident.preferredName || resident.name} — Room ${resident.room}`, 'success');
    console.info('[MobileCarerApp] Scan-in', { resident: resident.name, room: resident.room });
  }, [showToast]);

  const handleScanOut = useCallback(() => {
    if (scannedResident && scanStartTime) {
      const elapsed = Math.round((new Date() - scanStartTime) / 60000);
      const logEntry = {
        id: `CM-${Date.now()}`,
        residentId: scannedResident.id,
        residentName: scannedResident.name,
        room: scannedResident.room,
        scanIn: scanStartTime.toISOString(),
        scanOut: new Date().toISOString(),
        durationMinutes: elapsed,
        careWorker: currentUser?.name || 'Demo Carer',
        role: currentUser?.role || 'pca',
      };
      setCareMinutesLog(prev => [...prev, logEntry]);
      showToast(`Scanned out: ${scannedResident.preferredName || scannedResident.name} — ${elapsed} min logged`, 'info');
      console.info('[MobileCarerApp] Scan-out — care minutes logged', { minutes: elapsed, resident: scannedResident.name });
    }
    setScannedResident(null);
    setScanStartTime(null);
  }, [scannedResident, scanStartTime, currentUser, showToast]);

  const handleNavTap = useCallback((tabId) => {
    setActiveTab(tabId);
    if (tabId !== 'more') setMoreSubPanel(null);
  }, []);

  const renderContent = () => {
    if (activeTab === 'more') {
      if (moreSubPanel === 'incident') return <IncidentPanel resident={scannedResident} optimization={optimization} />;
      if (moreSubPanel === 'hardware') return <HardwarePanel optimization={optimization} />;
      if (moreSubPanel === 'settings') return <SettingsPanel />;
      return <MorePanel onSelect={setMoreSubPanel} />;
    }
    if (activeTab === 'scan') return <ScanPanel onScanIn={handleScanIn} isOnline={isOnline} optimization={optimization} />;
    if (activeTab === 'tasks') return <TasksPanel resident={scannedResident} optimization={optimization} />;
    if (activeTab === 'chart') return <ChartingPanel resident={scannedResident} careMinutesLog={careMinutesLog} optimization={optimization} />;
    if (activeTab === 'mar') return <MARPanel resident={scannedResident} userRole={currentUser?.role} optimization={optimization} />;
    return null;
  };

  return (
    <div className="relative pb-20 min-h-[600px]">
      {/* Mobile App Chrome */}
      <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex justify-between items-start">
          <div>
            <h1 className={`${optimization.fontSize === 'text-sm' ? 'text-2xl' : 'text-3xl'} font-bold flex items-center gap-3`}>
              Point-of-Care App
              <Smartphone className={`${optimization.iconSize}`} />
            </h1>
            <p className="mt-1 opacity-90">Carer mobile application</p>
            <div className="flex items-center gap-4 mt-3 text-sm">
              <span className="flex items-center gap-1">
                {isOnline ? <Wifi className="w-4 h-4 text-green-300" /> : <WifiOff className="w-4 h-4 text-red-300" />}
                {isOnline ? 'Online' : 'Offline — data cached'}
              </span>
              {scannedResident && (
                <span className="flex items-center gap-1 bg-white/20 px-3 py-1 rounded-full">
                  <MapPin className="w-3 h-3" /> In room: {scannedResident.room}
                  <span className="ml-1 text-xs opacity-80">({Math.round((new Date() - scanStartTime) / 60000)}m)</span>
                </span>
              )}
            </div>
          </div>
          {scannedResident && (
            <button onClick={handleScanOut} className="bg-red-500 text-white px-4 py-2 rounded-xl font-medium hover:bg-red-600 flex items-center gap-2">
              <LogOut className="w-4 h-4" /> Scan Out
            </button>
          )}
        </div>
      </div>

      {/* Back button for More sub-panels */}
      {activeTab === 'more' && moreSubPanel && (
        <button
          onClick={() => setMoreSubPanel(null)}
          className="flex items-center gap-1 mt-4 mb-2 text-sm text-indigo-600 font-medium hover:text-indigo-800 transition-colors"
        >
          <ChevronRight className="w-4 h-4 rotate-180" /> Back to More
        </button>
      )}

      {/* Content Area */}
      <div className="mt-4">
        {renderContent()}
      </div>

      {/* ─── Fixed Bottom Navigation Bar ─── */}
      <div className="sticky bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]" style={{ minHeight: 60 }}>
        <nav className="flex items-stretch justify-around" role="tablist" aria-label="Mobile navigation">
          {NAV_ITEMS.map(item => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => handleNavTap(item.id)}
                className={`flex flex-col items-center justify-center flex-1 py-2 transition-colors relative ${
                  isActive ? 'text-indigo-600' : 'text-gray-400 active:text-gray-600'
                }`}
                style={{ minHeight: 60, minWidth: 44 }}
              >
                {isActive && (
                  <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-indigo-600 rounded-full" />
                )}
                <Icon className={`w-5 h-5 ${isActive ? 'text-indigo-600' : ''}`} />
                <span className={`text-[10px] mt-0.5 font-medium ${isActive ? 'text-indigo-600' : ''}`}>{item.label}</span>
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
};

// ─── Scan Panel (NFC / QR / BLE) ─────────────────────────────────────

const ScanPanel = ({ onScanIn, isOnline, optimization }) => {
  const [scanMode, setScanMode] = useState('nfc');
  const [scanning, setScanning] = useState(false);
  const [manualRoom, setManualRoom] = useState('');
  const [recentResidents, setRecentResidents] = useState(() => getRecentResidents());

  const mockResidents = [
    { id: 'res-001', name: 'Margaret Johnson', preferredName: 'Maggie', room: '12A', photo: null, alerts: ['Fall risk: High', 'Allergy: Penicillin'], allergies: ['Penicillin'], diet: 'Soft texture', mobility: 'Walking frame', continence: 'Continent', communication: 'Verbal — slightly hard of hearing (left ear)', carePlanSummary: 'Focus on mobility maintenance, fall prevention, and social engagement.', todaysTasks: 5 },
    { id: 'res-002', name: 'Robert Williams', preferredName: 'Bob', room: '14B', photo: null, alerts: ['Diabetes Type 2', 'Epilepsy protocol'], allergies: ['Sulfa drugs'], diet: 'Diabetic', mobility: 'Wheelchair', continence: 'Assisted', communication: 'Verbal', carePlanSummary: 'Blood glucose monitoring QID. Seizure precautions. Skin integrity checks.', todaysTasks: 7 },
    { id: 'res-003', name: 'Dorothy Chen', preferredName: 'Dot', room: '8C', photo: null, alerts: ['Palliative pathway'], allergies: [], diet: 'Modified fluids', mobility: 'Bed-bound', continence: 'Full care', communication: 'Non-verbal — responds to touch and music', carePlanSummary: 'Comfort-focused care. 2-hourly repositioning. Family wishes documented.', todaysTasks: 8 },
  ];

  const handleScan = async () => {
    setScanning(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    const resident = mockResidents[Math.floor(Math.random() * mockResidents.length)];
    setScanning(false);
    onScanIn(resident);
    setRecentResidents(getRecentResidents());
  };

  const handleManualEntry = () => {
    const resident = mockResidents.find(r => r.room.toLowerCase() === manualRoom.toLowerCase());
    if (resident) {
      onScanIn(resident);
      setRecentResidents(getRecentResidents());
    }
    setManualRoom('');
  };

  const handleRecentSelect = (recent) => {
    const fullResident = mockResidents.find(r => r.id === recent.id) || recent;
    console.info('[MobileCarerApp] Quick-select recent resident', recent.name);
    onScanIn(fullResident);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">Scan Room Tag</h2>

      {/* Recent Residents Quick-Select */}
      {recentResidents.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Recent Residents</p>
          <div className="flex gap-4 overflow-x-auto pb-2">
            {recentResidents.map(r => (
              <button
                key={r.id}
                onClick={() => handleRecentSelect(r)}
                className="flex flex-col items-center gap-1.5 shrink-0 group"
                style={{ minWidth: 64 }}
              >
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white font-bold text-sm shadow-md group-active:scale-95 transition-transform ring-2 ring-white">
                  {(r.preferredName || r.name).charAt(0)}
                </div>
                <span className="text-[11px] font-medium text-gray-700 leading-tight text-center max-w-[64px] truncate">
                  {r.preferredName || r.name.split(' ')[0]}
                </span>
                <span className="text-[10px] text-gray-400">Rm {r.room}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Scan Mode Selector */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { id: 'nfc', label: 'NFC', icon: Nfc, desc: 'Tap device on tag' },
          { id: 'qr', label: 'QR Code', icon: QrCode, desc: 'Scan with camera' },
          { id: 'ble', label: 'BLE Beacon', icon: Radio, desc: 'Auto-detect nearby' },
        ].map(mode => {
          const Icon = mode.icon;
          return (
            <button
              key={mode.id}
              onClick={() => setScanMode(mode.id)}
              className={`p-4 rounded-xl border-2 transition-all text-center ${
                scanMode === mode.id ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-indigo-300'
              }`}
            >
              <Icon className={`w-8 h-8 mx-auto mb-2 ${scanMode === mode.id ? 'text-indigo-600' : 'text-gray-400'}`} />
              <p className="font-medium text-sm">{mode.label}</p>
              <p className="text-xs text-gray-500 mt-1">{mode.desc}</p>
            </button>
          );
        })}
      </div>

      {/* Scan Button */}
      <div className="flex flex-col items-center py-8">
        <button
          onClick={handleScan}
          disabled={scanning}
          className={`w-40 h-40 rounded-full flex flex-col items-center justify-center gap-2 transition-all shadow-lg ${
            scanning
              ? 'bg-indigo-400 animate-pulse'
              : 'bg-indigo-600 hover:bg-indigo-700 hover:shadow-xl active:scale-95'
          } text-white`}
        >
          {scanning ? (
            <>
              <div className="w-10 h-10 border-3 border-white/30 border-t-white rounded-full animate-spin" />
              <span className="text-sm font-medium">Scanning...</span>
            </>
          ) : (
            <>
              {scanMode === 'nfc' && <Nfc className="w-12 h-12" />}
              {scanMode === 'qr' && <QrCode className="w-12 h-12" />}
              {scanMode === 'ble' && <Radio className="w-12 h-12" />}
              <span className="text-sm font-medium mt-1">Tap to Scan</span>
            </>
          )}
        </button>
        <p className="text-sm text-gray-500 mt-4">
          {scanMode === 'nfc' && 'Hold your device against the NFC tag on the door frame'}
          {scanMode === 'qr' && 'Point your camera at the QR code on the door frame'}
          {scanMode === 'ble' && 'Walk close to the room — beacon will be detected automatically'}
        </p>
      </div>

      {/* Manual Fallback */}
      <div className="bg-gray-50 rounded-xl p-4">
        <p className="text-sm font-medium text-gray-700 mb-2">Manual Room Entry (fallback)</p>
        <div className="flex gap-2">
          <input
            type="text"
            value={manualRoom}
            onChange={e => setManualRoom(e.target.value)}
            placeholder="Enter room number..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
          />
          <button onClick={handleManualEntry} className="bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-indigo-700">
            Go
          </button>
        </div>
      </div>

      {/* Offline Status */}
      {!isOnline && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-center gap-3">
          <WifiOff className="w-5 h-5 text-yellow-600" />
          <div>
            <p className="font-medium text-yellow-800">Offline Mode Active</p>
            <p className="text-sm text-yellow-600">Using cached resident data. Changes will sync when connection is restored.</p>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Tasks Panel ──────────────────────────────────────────────────────

const TasksPanel = ({ resident, optimization }) => {
  const { showToast } = useToast();
  const [tasks, setTasks] = useState([
    { id: 't1', type: 'shower', label: 'Morning Shower', scheduled: '07:00', status: 'pending', priority: 'routine', notes: '' },
    { id: 't2', type: 'meal', label: 'Breakfast Assistance', scheduled: '08:00', status: 'completed', priority: 'routine', notes: 'Ate 80% of meal. Enjoyed porridge.' },
    { id: 't3', type: 'repositioning', label: 'Repositioning Check', scheduled: '09:00', status: 'pending', priority: 'routine', notes: '' },
    { id: 't4', type: 'toileting', label: 'Toileting Assistance', scheduled: '10:00', status: 'pending', priority: 'routine', notes: '' },
    { id: 't5', type: 'meal', label: 'Morning Tea', scheduled: '10:30', status: 'pending', priority: 'routine', notes: '' },
  ]);

  if (!resident) {
    return (
      <div className="text-center py-12 text-gray-500">
        <ClipboardList className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p className="font-medium">No resident scanned</p>
        <p className="text-sm mt-1">Scan a room tag to view tasks</p>
      </div>
    );
  }

  const toggleTask = (taskId) => {
    setTasks(prev => prev.map(t => {
      if (t.id !== taskId) return t;
      const newStatus = t.status === 'completed' ? 'pending' : 'completed';
      if (newStatus === 'completed') {
        haptic(50);
        showToast(`Task completed: ${t.label}`, 'success');
        console.info('[MobileCarerApp] Task completed', { taskId, label: t.label });
      }
      return { ...t, status: newStatus, completedAt: newStatus === 'completed' ? new Date().toISOString() : null };
    }));
  };

  const handleSwipeComplete = (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    if (task && task.status !== 'completed') {
      toggleTask(taskId);
    }
  };

  const handleRefresh = async () => {
    console.info('[MobileCarerApp] Refreshing task list...');
    await new Promise(resolve => setTimeout(resolve, 800));
    setTasks(prev => prev.map(t => ({ ...t })));
    showToast('Tasks refreshed', 'info');
  };

  const completedCount = tasks.filter(t => t.status === 'completed').length;

  const taskIcons = {
    shower: Droplets,
    meal: Utensils,
    repositioning: RotateCcw,
    toileting: Droplets,
  };

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="space-y-4">
        <ResidentSummaryBar resident={resident} />

        {/* Progress */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">Task Progress</span>
            <span className="text-sm font-medium text-indigo-600">{completedCount}/{tasks.length}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div className="bg-indigo-600 h-3 rounded-full transition-all" style={{ width: `${(completedCount / tasks.length) * 100}%` }} />
          </div>
        </div>

        {/* Task List with swipe-to-complete */}
        <div className="space-y-2">
          {tasks.map(task => {
            const Icon = taskIcons[task.type] || CheckCircle;
            const isCompleted = task.status === 'completed';
            return (
              <SwipeableTaskItem
                key={task.id}
                onSwipeComplete={() => handleSwipeComplete(task.id)}
                disabled={isCompleted}
              >
                <div className={`bg-white rounded-xl p-4 border-2 transition-all ${isCompleted ? 'border-green-200 bg-green-50/30' : 'border-gray-200'}`}>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => toggleTask(task.id)}
                      className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all shrink-0 ${isCompleted ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300 hover:border-indigo-500'}`}
                      style={{ minWidth: 44, minHeight: 44, padding: 8 }}
                    >
                      {isCompleted && <CheckCircle className="w-5 h-5" />}
                    </button>
                    <Icon className={`w-5 h-5 shrink-0 ${isCompleted ? 'text-green-600' : 'text-gray-400'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${isCompleted ? 'text-green-700 line-through' : 'text-gray-900'}`}>{task.label}</span>
                        <span className="text-xs text-gray-400">{task.scheduled}</span>
                      </div>
                      {task.notes && <p className="text-xs text-gray-500 mt-0.5">{task.notes}</p>}
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
                  </div>
                </div>
              </SwipeableTaskItem>
            );
          })}
        </div>

        <p className="text-center text-xs text-gray-400 pt-2">Swipe a task right to complete &middot; Pull down to refresh</p>
      </div>
    </PullToRefresh>
  );
};

// ─── Charting Panel ───────────────────────────────────────────────────

const ChartingPanel = ({ resident, careMinutesLog, optimization }) => {
  const { showToast } = useToast();
  const [chartType, setChartType] = useState('shower');
  const [notes, setNotes] = useState('');
  const [isRecording, setIsRecording] = useState(false);

  const chartTypes = [
    { id: 'shower', label: 'Shower/Bath', icon: Droplets },
    { id: 'meal', label: 'Meals', icon: Utensils },
    { id: 'toileting', label: 'Toileting', icon: Droplets },
    { id: 'repositioning', label: 'Repositioning', icon: RotateCcw },
    { id: 'vitals', label: 'Vitals', icon: Activity },
    { id: 'progress', label: 'Progress Note', icon: FileText },
  ];

  const handleVoiceDictation = () => {
    if (isRecording) {
      setIsRecording(false);
      setNotes(prev => prev + ' [Voice note: Resident was cooperative during shower. Skin integrity intact. No new concerns.]');
      console.info('[MobileCarerApp] Voice dictation stopped — text appended');
    } else {
      setIsRecording(true);
      console.info('[MobileCarerApp] Voice dictation started (Web Speech API stub)');
    }
  };

  const handleSave = () => {
    if (!notes.trim()) return;
    console.info('[MobileCarerApp] Chart entry saved', { type: chartType, resident: resident?.name });
    haptic(50);
    showToast(`Chart entry saved: ${chartType}`, 'success');
    setNotes('');
  };

  if (!resident) {
    return (
      <div className="text-center py-12 text-gray-500">
        <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p className="font-medium">No resident scanned</p>
        <p className="text-sm mt-1">Scan a room tag to start charting</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <ResidentSummaryBar resident={resident} />

      {/* Chart Type Selection */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        {chartTypes.map(ct => {
          const Icon = ct.icon;
          return (
            <button
              key={ct.id}
              onClick={() => setChartType(ct.id)}
              className={`p-3 rounded-xl border-2 transition-all text-center ${
                chartType === ct.id ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-indigo-300'
              }`}
            >
              <Icon className={`w-5 h-5 mx-auto mb-1 ${chartType === ct.id ? 'text-indigo-600' : 'text-gray-400'}`} />
              <span className="text-xs font-medium">{ct.label}</span>
            </button>
          );
        })}
      </div>

      {/* Charting Form */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <h3 className="font-semibold mb-3 capitalize">{chartType} Entry</h3>
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Clock className="w-4 h-4" />
            <span>Timestamp: {new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })} — {new Date().toLocaleDateString('en-AU')}</span>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Notes</label>
            <div className="relative">
              <textarea
                rows={4}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-indigo-500 pr-12"
                placeholder="Record observations..."
              />
              <button
                onClick={handleVoiceDictation}
                className={`absolute right-2 bottom-2 p-2 rounded-lg transition ${isRecording ? 'bg-red-500 text-white animate-pulse' : 'bg-gray-100 text-gray-600 hover:bg-indigo-100'}`}
                title={isRecording ? 'Stop recording' : 'Start voice dictation'}
              >
                {isRecording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </button>
            </div>
            {isRecording && <p className="text-xs text-red-600 mt-1 animate-pulse">Recording... speak clearly into device microphone</p>}
          </div>

          <div className="flex justify-end gap-2">
            <button onClick={handleSave} className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-indigo-700 flex items-center gap-2">
              <Save className="w-4 h-4" /> Save Entry
            </button>
          </div>
        </div>
      </div>

      {/* Care Minutes Log */}
      {careMinutesLog.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h3 className="font-semibold mb-3 flex items-center gap-2"><Clock className="w-5 h-5 text-indigo-600" /> Care Minutes Today</h3>
          <div className="space-y-2">
            {careMinutesLog.map(entry => (
              <div key={entry.id} className="flex items-center justify-between p-3 rounded-lg bg-indigo-50">
                <div>
                  <span className="font-medium text-sm">{entry.residentName}</span>
                  <span className="text-xs text-gray-500 ml-2">Room {entry.room}</span>
                </div>
                <span className="font-semibold text-indigo-600">{entry.durationMinutes} min</span>
              </div>
            ))}
            <div className="flex justify-between pt-2 border-t border-indigo-200 mt-2">
              <span className="font-semibold text-sm">Total</span>
              <span className="font-bold text-indigo-600">{careMinutesLog.reduce((sum, e) => sum + e.durationMinutes, 0)} min</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── MAR Panel ────────────────────────────────────────────────────────

const MARPanel = ({ resident, userRole, optimization }) => {
  const { showToast } = useToast();
  const [medications, setMedications] = useState([
    { id: 'm1', name: 'Amlodipine 5mg', route: 'Oral', frequency: 'Daily', time: '08:00', status: 'due', s8: false },
    { id: 'm2', name: 'Metformin 500mg', route: 'Oral', frequency: 'BD', time: '08:00', status: 'given', givenAt: '08:05', givenBy: 'RN Kim', s8: false },
    { id: 'm3', name: 'Paracetamol 500mg', route: 'Oral', frequency: 'QID PRN', time: 'PRN', status: 'available', s8: false },
    { id: 'm4', name: 'Oxycodone 5mg', route: 'Oral', frequency: 'BD PRN', time: 'PRN', status: 'available', s8: true },
    { id: 'm5', name: 'Enoxaparin 40mg', route: 'SC Injection', frequency: 'Daily', time: '18:00', status: 'due', s8: false },
  ]);

  const [dualSignRequired, setDualSignRequired] = useState(false);
  const [pendingAdminId, setPendingAdminId] = useState(null);

  const isRN = userRole === 'admin' || userRole === 'nurse' || userRole === 'rn';

  if (!resident) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Pill className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p className="font-medium">No resident scanned</p>
        <p className="text-sm mt-1">Scan a room tag to view MAR</p>
      </div>
    );
  }

  if (!isRN) {
    return (
      <div className="text-center py-12">
        <Shield className="w-12 h-12 mx-auto mb-3 text-orange-400" />
        <p className="font-medium text-orange-600">RN Access Required</p>
        <p className="text-sm text-gray-500 mt-1">Medication administration requires Registered Nurse credentials</p>
      </div>
    );
  }

  const confirmAdminister = (medId) => {
    setMedications(prev => prev.map(m =>
      m.id === medId
        ? { ...m, status: 'given', givenAt: new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }), givenBy: 'You' }
        : m
    ));
    haptic(80);
    const med = medications.find(m => m.id === medId);
    showToast(`Medication administered: ${med?.name}`, 'success');
    console.info('[MobileCarerApp] Medication administered', { medId, name: med?.name, resident: resident.name });
    setDualSignRequired(false);
    setPendingAdminId(null);
  };

  const handleAdminister = (medId) => {
    const med = medications.find(m => m.id === medId);
    if (med?.s8) {
      setDualSignRequired(true);
      setPendingAdminId(medId);
    } else {
      confirmAdminister(medId);
    }
  };

  const handleRefresh = async () => {
    console.info('[MobileCarerApp] Refreshing MAR data...');
    await new Promise(resolve => setTimeout(resolve, 800));
    setMedications(prev => prev.map(m => ({ ...m })));
    showToast('MAR refreshed', 'info');
  };

  const statusColors = {
    due: 'bg-yellow-100 text-yellow-700 border-yellow-300',
    given: 'bg-green-100 text-green-700 border-green-300',
    withheld: 'bg-red-100 text-red-700 border-red-300',
    refused: 'bg-orange-100 text-orange-700 border-orange-300',
    available: 'bg-blue-100 text-blue-700 border-blue-300',
  };

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="space-y-4">
        <ResidentSummaryBar resident={resident} />

        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold">Medication Administration Record</h2>
          <span className="text-sm text-gray-500 flex items-center gap-1"><Calendar className="w-4 h-4" /> {new Date().toLocaleDateString('en-AU')}</span>
        </div>

        {/* Allergy Alert with Tooltip */}
        {resident.allergies?.length > 0 && (
          <Tooltip content="Verified allergy list — always confirm with resident before administering" position="bottom">
            <div className="bg-red-50 border-2 border-red-300 rounded-xl p-3 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
              <div>
                <span className="font-bold text-red-700">ALLERGIES: </span>
                {resident.allergies.map((allergy, i) => (
                  <Tooltip key={i} content={`Known allergy — contraindicated medications will be flagged`} position="top">
                    <span className="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-red-200 text-red-800 font-bold mr-1 cursor-help">
                      {allergy}
                    </span>
                  </Tooltip>
                ))}
              </div>
            </div>
          </Tooltip>
        )}

        {/* Medication List */}
        <div className="space-y-3">
          {medications.map(med => (
            <div key={med.id} className={`bg-white rounded-xl p-4 border-2 ${med.s8 ? 'border-red-200' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900">{med.name}</span>
                    {med.s8 && (
                      <Tooltip content="Schedule 8 controlled substance — dual RN witness signature required for administration" position="top">
                        <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700 font-bold cursor-help">S8</span>
                      </Tooltip>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1 text-xs text-gray-500">
                    <span>{med.route}</span>
                    <span>{med.frequency}</span>
                    <span>{med.time}</span>
                  </div>
                  {med.givenBy && <p className="text-xs text-green-600 mt-1">Given at {med.givenAt} by {med.givenBy}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 text-xs rounded-full font-medium border ${statusColors[med.status]}`}>
                    {med.status.toUpperCase()}
                  </span>
                  {(med.status === 'due' || med.status === 'available') && (
                    <div className="flex gap-1">
                      <button onClick={() => handleAdminister(med.id)} className="p-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200" title="Given" style={{ minWidth: 44, minHeight: 44 }}>
                        <CheckCircle className="w-4 h-4" />
                      </button>
                      <button className="p-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200" title="Withheld" style={{ minWidth: 44, minHeight: 44 }}>
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-xs text-gray-400 pt-1">Pull down to refresh MAR data</p>

        {/* S8 Dual Sign Modal */}
        {dualSignRequired && (
          <div className="bg-red-50 border-2 border-red-300 rounded-xl p-5">
            <h3 className="font-bold text-red-800 flex items-center gap-2"><Shield className="w-5 h-5" /> S8 Dual-Sign Required</h3>
            <p className="text-sm text-red-700 mt-2">Schedule 8 medication requires a second RN witness to sign.</p>
            <div className="mt-4 space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Witness RN Name</label>
                <input type="text" className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg" placeholder="Enter witness name..." />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Witness PIN</label>
                <input type="password" className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg" placeholder="Enter witness PIN..." />
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setDualSignRequired(false); setPendingAdminId(null); }} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={() => confirmAdminister(pendingAdminId)} className="bg-red-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-red-700">Confirm & Sign</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PullToRefresh>
  );
};

// ─── Incident Panel ───────────────────────────────────────────────────

const IncidentPanel = ({ resident, optimization }) => {
  const { showToast } = useToast();
  const [form, setForm] = useState({ type: '', severity: 'minor', description: '', injuryOccurred: false, witnessName: '' });

  const incidentTypes = [
    { id: 'fall', label: 'Fall' },
    { id: 'medication_error', label: 'Medication Error' },
    { id: 'behavior', label: 'Behavioural' },
    { id: 'skin_integrity', label: 'Skin Integrity' },
    { id: 'infection', label: 'Infection' },
    { id: 'missing_person', label: 'Missing Person' },
    { id: 'other', label: 'Other' },
  ];

  const handleSubmit = () => {
    if (!form.type || !form.description) return;
    console.info('[MobileCarerApp] Incident reported', { type: form.type, resident: resident?.name });
    haptic(100);
    showToast('Incident report submitted — manager notified', 'success');
    setForm({ type: '', severity: 'minor', description: '', injuryOccurred: false, witnessName: '' });
  };

  return (
    <div className="space-y-4">
      {resident && <ResidentSummaryBar resident={resident} />}

      <h2 className="text-lg font-semibold">Quick Incident Report</h2>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-4">
        <div>
          <label className="text-sm font-medium text-gray-700">Incident Type</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
            {incidentTypes.map(t => (
              <button
                key={t.id}
                onClick={() => setForm(p => ({ ...p, type: t.id }))}
                className={`p-2 rounded-lg border-2 text-sm font-medium transition ${
                  form.type === t.id ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-600 hover:border-indigo-300'
                }`}
              >{t.label}</button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">Severity</label>
          <div className="flex gap-2 mt-2">
            {['minor', 'moderate', 'severe', 'critical'].map(s => (
              <button
                key={s}
                onClick={() => setForm(p => ({ ...p, severity: s }))}
                className={`px-4 py-2 rounded-lg border-2 text-sm font-medium capitalize transition ${
                  form.severity === s ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-indigo-300'
                }`}
              >{s}</button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-gray-700">Injury Occurred?</label>
          <button
            onClick={() => setForm(p => ({ ...p, injuryOccurred: !p.injuryOccurred }))}
            className={`w-12 h-6 rounded-full transition-colors ${form.injuryOccurred ? 'bg-red-500' : 'bg-gray-300'}`}
          >
            <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${form.injuryOccurred ? 'translate-x-6' : 'translate-x-0.5'}`} />
          </button>
          <span className="text-sm text-gray-600">{form.injuryOccurred ? 'Yes' : 'No'}</span>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">Description</label>
          <textarea
            rows={4}
            value={form.description}
            onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg resize-none"
            placeholder="Describe what happened..."
          />
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">Witness (if any)</label>
          <input
            type="text"
            value={form.witnessName}
            onChange={e => setForm(p => ({ ...p, witnessName: e.target.value }))}
            className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="Witness name..."
          />
        </div>

        <div className="flex justify-end">
          <button onClick={handleSubmit} className="bg-red-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-red-700 flex items-center gap-2">
            <Send className="w-4 h-4" /> Submit Incident
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── More Panel ───────────────────────────────────────────────────────

const MorePanel = ({ onSelect }) => {
  const moreItems = [
    { id: 'incident', label: 'Incident Report', icon: AlertTriangle, desc: 'Report falls, errors & events', color: 'text-red-600 bg-red-50' },
    { id: 'hardware', label: 'Hardware Config', icon: Nfc, desc: 'NFC, QR & BLE setup', color: 'text-blue-600 bg-blue-50' },
    { id: 'settings', label: 'App Settings', icon: Settings, desc: 'Preferences & sync options', color: 'text-gray-600 bg-gray-100' },
  ];

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold">More</h2>
      {moreItems.map(item => {
        const Icon = item.icon;
        return (
          <button
            key={item.id}
            onClick={() => onSelect(item.id)}
            className="w-full flex items-center gap-4 bg-white rounded-xl p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow active:scale-[0.98]"
          >
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${item.color}`}>
              <Icon className="w-5 h-5" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-semibold text-gray-900 text-sm">{item.label}</p>
              <p className="text-xs text-gray-500">{item.desc}</p>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-300" />
          </button>
        );
      })}
    </div>
  );
};

// ─── Settings Panel (placeholder for More > Settings) ─────────────────

const SettingsPanel = () => (
  <div className="space-y-4">
    <h2 className="text-lg font-semibold">App Settings</h2>
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-4">
      {[
        { label: 'Auto-sync when online', defaultOn: true },
        { label: 'Sound on scan', defaultOn: true },
        { label: 'Haptic feedback', defaultOn: true },
        { label: 'High-contrast mode', defaultOn: false },
        { label: 'Large text', defaultOn: false },
      ].map((setting, i) => (
        <SettingToggle key={i} label={setting.label} defaultOn={setting.defaultOn} />
      ))}
    </div>
  </div>
);

const SettingToggle = ({ label, defaultOn }) => {
  const [on, setOn] = useState(defaultOn);
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      <button
        onClick={() => setOn(v => !v)}
        className={`w-11 h-6 rounded-full transition-colors ${on ? 'bg-indigo-600' : 'bg-gray-300'}`}
      >
        <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${on ? 'translate-x-5' : 'translate-x-0.5'}`} />
      </button>
    </div>
  );
};

// ─── Hardware Panel ───────────────────────────────────────────────────

const HardwarePanel = ({ optimization }) => {
  const nfcModels = HardwareConfigService.getCompatibleNFCModels();
  const bleModels = HardwareConfigService.getCompatibleBLEModels();
  const nfcGuide = HardwareConfigService.getNFCDeploymentGuide();
  const qrGuide = HardwareConfigService.getQRPlacementGuide();
  const bleRanges = HardwareConfigService.getBLERangeConfiguration();
  const byodPolicy = HardwareConfigService.getBYODPolicy();

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">Hardware Configuration</h2>

      {/* NFC Tags */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="font-semibold flex items-center gap-2 mb-4"><Nfc className="w-5 h-5 text-indigo-600" /> NFC Tags</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-2 font-medium text-gray-700">Model</th>
                <th className="text-left py-2 px-2 font-medium text-gray-700">Memory</th>
                <th className="text-left py-2 px-2 font-medium text-gray-700">Range</th>
                <th className="text-left py-2 px-2 font-medium text-gray-700">Price</th>
                <th className="text-left py-2 px-2 font-medium text-gray-700"></th>
              </tr>
            </thead>
            <tbody>
              {nfcModels.map(m => (
                <tr key={m.model} className="border-b border-gray-100">
                  <td className="py-2 px-2 font-medium">{m.model}</td>
                  <td className="py-2 px-2">{m.memory}</td>
                  <td className="py-2 px-2">{m.range}</td>
                  <td className="py-2 px-2">${m.priceAUD.toFixed(2)}</td>
                  <td className="py-2 px-2">{m.recommended && <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full font-medium">Recommended</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Deployment Guide</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            {nfcGuide.placement.map((step, i) => (
              <li key={i} className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" /> {step}</li>
            ))}
          </ul>
        </div>
      </div>

      {/* QR Codes */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="font-semibold flex items-center gap-2 mb-4"><QrCode className="w-5 h-5 text-indigo-600" /> QR Codes</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Printing</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              {qrGuide.printing.map((step, i) => <li key={i} className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" /> {step}</li>)}
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Placement</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              {qrGuide.placement.map((step, i) => <li key={i} className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" /> {step}</li>)}
            </ul>
          </div>
        </div>
      </div>

      {/* BLE Beacons */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="font-semibold flex items-center gap-2 mb-4"><Radio className="w-5 h-5 text-indigo-600" /> BLE Beacons</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-2 font-medium text-gray-700">Model</th>
                <th className="text-left py-2 px-2 font-medium text-gray-700">Battery</th>
                <th className="text-left py-2 px-2 font-medium text-gray-700">Range</th>
                <th className="text-left py-2 px-2 font-medium text-gray-700">Price</th>
                <th className="text-left py-2 px-2 font-medium text-gray-700"></th>
              </tr>
            </thead>
            <tbody>
              {bleModels.map(m => (
                <tr key={m.model} className="border-b border-gray-100">
                  <td className="py-2 px-2 font-medium">{m.model}</td>
                  <td className="py-2 px-2">{m.battery}</td>
                  <td className="py-2 px-2">{m.range}</td>
                  <td className="py-2 px-2">${m.priceAUD.toFixed(2)}</td>
                  <td className="py-2 px-2">{m.recommended && <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full font-medium">Recommended</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Range Configuration</h4>
          <div className="grid md:grid-cols-3 gap-3">
            {Object.entries(bleRanges).map(([key, config]) => (
              <div key={key} className="p-3 rounded-lg bg-indigo-50 border border-indigo-200">
                <p className="font-medium text-sm capitalize">{key.replace(/([A-Z])/g, ' $1')}</p>
                <p className="text-xs text-gray-600 mt-1">{config.expectedRange}</p>
                <p className="text-xs text-indigo-600">{config.useCase}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Device Management / MDM */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="font-semibold flex items-center gap-2 mb-4"><Smartphone className="w-5 h-5 text-indigo-600" /> Device Management</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-sm mb-2">Facility-Owned Devices (MDM)</h4>
            <div className="text-sm text-gray-600 space-y-1">
              {HardwareConfigService._getMDMProfile().policies.map((policy, i) => (
                <div key={i} className="flex items-center gap-2"><Shield className="w-3 h-3 text-indigo-500" /> {policy}</div>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-medium text-sm mb-2">BYOD Policy</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              {byodPolicy.requirements.slice(0, 5).map((req, i) => (
                <li key={i} className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-green-500 shrink-0 mt-1" /> {req}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Shared Resident Summary Bar ──────────────────────────────────────

const ResidentSummaryBar = ({ resident }) => {
  if (!resident) return null;

  return (
    <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white font-bold text-lg shrink-0 shadow">
          {(resident.preferredName || resident.name).charAt(0)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-gray-900">{resident.preferredName || resident.name}</h3>
            <span className="text-xs text-gray-500">Room {resident.room}</span>
          </div>
          {resident.alerts?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {resident.alerts.map((alert, i) => {
                const isAllergy = alert.toLowerCase().includes('allergy');
                return (
                  <Tooltip key={i} content={isAllergy ? `Known allergy — verify before any medication administration` : `Active clinical alert — review care plan`} position="top">
                    <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700 font-medium flex items-center gap-1 cursor-help">
                      <AlertTriangle className="w-3 h-3" />{alert}
                    </span>
                  </Tooltip>
                );
              })}
            </div>
          )}
          <div className="flex flex-wrap gap-3 mt-1 text-xs text-gray-600">
            {resident.diet && <span>Diet: {resident.diet}</span>}
            {resident.mobility && <span>Mobility: {resident.mobility}</span>}
            {resident.communication && <span>Comms: {resident.communication}</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileCarerApp;
